/**
 * 출력물 부록 슬라이드(#7) 모델 — 순수 함수(직렬화/역직렬화/정규화).
 * 슬라이드: { id, type:'photo'|'blank', imgId, caption }.
 *   - photo: imgId(building_print_slide_img PK) + caption. 서빙 = VUE_APP_HOST_BACK + slideImagePath(imgId).
 *   - blank: 빈 A4 페이지.
 * 직렬화 형식: [[id,type,imgId,caption],...] (compact tuple, investment.mjs rmrows 와 동형).
 * 단일물건 쿼리키 = 'slides'. 연쇄덱 = 'slides_<bid>' (네임스페이스는 호출측).
 *
 * 빈 상태 직렬화 규약: serializeSlides([]) === null(= "직렬화할 것 없음").
 *   URL 동기화 측은 null 일 때 '[]' 를 명시적으로 써서 "담당자가 세션에서 전부 지움" 이
 *   서버 PDF(=URL 로드)에서 저장된 DB 기본값으로 되돌아가지 않게 한다(키 누락=DB fallback 회피).
 *   저장(buildPrintSettings) 측은 null 이면 키 생략 → 빈값 저장 = DB 기본값 초기화.
 */
export const SLIDE_QUERY_KEY = 'slides';
const MAX_SLIDES = 20;
const MAX_CAPTION = 80;
const MAX_ID = 12;

// 슬라이드 배열 → 충돌 없는 다음 id ('s' + (최대 숫자 suffix + 1)).
export function nextSlideId(slides) {
  let max = 0;
  for (const s of (Array.isArray(slides) ? slides : [])) {
    const m = /^s(\d+)$/.exec(String(s && s.id != null ? s.id : ''));
    if (m) max = Math.max(max, Number(m[1]));
  }
  return 's' + (max + 1);
}

export function makeBlankSlide(slides) {
  return { id: nextSlideId(slides), type: 'blank', imgId: '', caption: '' };
}
export function makePhotoSlide(slides, imgId, caption) {
  return { id: nextSlideId(slides), type: 'photo', imgId: Number(imgId), caption: caption || '' };
}

// 직렬화: [[id,type,imgId,caption],...] JSON 문자열. 빈/유효슬라이드 0 → null.
export function serializeSlides(slides) {
  if (!Array.isArray(slides) || slides.length === 0) return null;
  const out = [];
  for (const s of slides) {
    if (!s) continue;
    const type = (s.type === 'blank') ? 'blank' : 'photo';
    const id = String(s.id == null ? '' : s.id).slice(0, MAX_ID);
    if (type === 'blank') { out.push([id, 'blank', '', '']); continue; }
    const n = parseInt(s.imgId, 10);
    if (!Number.isFinite(n) || n <= 0) continue;   // imgId 없는 사진 슬라이드 스킵
    out.push([id, 'photo', n, String(s.caption == null ? '' : s.caption).slice(0, MAX_CAPTION)]);
  }
  return out.length ? JSON.stringify(out) : null;
}

// 역직렬화: 문자열|배열 → 배열 또는 null.
export function parseSlidesParam(raw) {
  if (raw == null || raw === '') return null;
  let arr = raw;
  if (typeof raw === 'string') { try { arr = JSON.parse(raw); } catch (e) { return null; } }
  return Array.isArray(arr) ? arr : null;
}

// 정규화(단일 정규화 지점): 배열 → [{id,type,imgId,caption}]. 빈/중복 id → 충돌 없는 새 id.
export function rebuildSlides(arr) {
  const out = [];
  const seen = new Set();
  for (let i = 0; i < arr.length && out.length < MAX_SLIDES; i++) {
    const r = arr[i];
    let id, type, imgId, caption;
    if (Array.isArray(r)) { id = r[0]; type = r[1]; imgId = r[2]; caption = r[3]; }
    else if (r && typeof r === 'object') { id = r.id; type = r.type; imgId = r.imgId; caption = r.caption; }
    else continue;
    type = (type === 'blank') ? 'blank' : 'photo';
    const n = parseInt(imgId, 10);
    const img = (Number.isFinite(n) && n > 0) ? n : '';
    if (type === 'photo' && img === '') continue;   // 무효 사진 스킵
    id = String(id == null ? '' : id).slice(0, MAX_ID);
    if (id === '' || seen.has(id)) id = nextSlideId(out);   // 빈/중복 → 충돌 없는 새 id
    seen.add(id);
    caption = String(caption == null ? '' : caption).slice(0, MAX_CAPTION);
    out.push(type === 'blank'
      ? { id, type: 'blank', imgId: '', caption: '' }
      : { id, type: 'photo', imgId: img, caption });
  }
  return out;
}

// 쿼리값(raw 문자열|배열) → 정규화 슬라이드 배열(없거나 무효 → []).
export function slidesFromQueryValue(raw) {
  const arr = parseSlidesParam(raw);
  return arr ? rebuildSlides(arr) : [];
}

// 서빙 경로(host 제외). 컴포넌트가 VUE_APP_HOST_BACK 를 앞에 붙인다.
export function slideImagePath(imgId, w = 1200, q = 82) {
  return `/users/print-slide/image?id=${encodeURIComponent(imgId)}&w=${w}&q=${q}`;
}
